import ConciliationAdjustmentAmount from '../../../../../ui/ConciliationAdjustmentAmount'
import { getConciliationAdjustmentDisplay } from '../../../../../../utils/conciliationAdjustmentDisplay'
import { useContext, useEffect, useMemo, useState } from 'react'
import GlobalContext from '../../../../../../../context/global-context'
import businessApi from '../../../../../../../api/business.api'
import { TablesAPI } from '../../../../../../../api/table.api'
import { OrganizationAPI } from '../../../../../../../api/organization.api'
import ResizableModal from '../../../../../ui/ResizableModal'
import CustomButton from '../../../../../ui/CustomButton'
import { Title } from '../../../../../ui'
import { formatCurrency, formatDate } from '../../../../../../utils/formatter'
import TablaProvisionAgenteAduana from './TablaProvisionAgenteAduana'
import Lottie from 'lottie-react'
import LoaderAdm from '../../../../../../../assets/svgIcons/LoaderAdm.json'
import { sileo } from 'sileo'
import clsx from 'clsx'

const AGENTE_ADUANA_ACCOUNT_CODE = '1-1-6-8'
const AGENTE_ADUANA_ACCOUNT_NAME = 'Anticipo Agente de Aduana'
const BANCO_ORIGENES = new Set([6, 7])
const VINCULACION_ORIGENES = new Set([24, 26])
const MOVIMIENTOS_AGENTE_ADUANA = new Set([27, 30])
const MOVIMIENTO_AGENTE_ADUANA_LABELS = {
  27: 'Anticipo Agente Aduana',
  30: 'Devolución Agente Aduana'
}
const EMPTY_ROWS = []

const toArray = payload => {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.libros)) return payload.libros
  if (Array.isArray(payload?.content)) return payload.content
  if (payload && typeof payload === 'object') {
    return Object.values(payload)
      .flat()
      .filter(item => item && typeof item === 'object')
  }
  return []
}

const getRowId = row =>
  row?.idPrincipal ??
  row?.id ??
  row?.libro?.idPrincipal ??
  row?.libro?.id ??
  row?.din?.idPrincipal ??
  row?.din?.id
const getRowIdentifiers = row =>
  [
    row?.idPrincipal,
    row?.id,
    row?.libro?.idPrincipal,
    row?.libro?.id,
    row?.din?.idPrincipal,
    row?.din?.id
  ]
    .filter(value => value != null)
    .map(String)
const isBankMovement = row =>
  [row?.origenTabla, row?.tabla, row?.tablaPrincipal]
    .map(Number)
    .some(origin => BANCO_ORIGENES.has(origin))
const getRowAmount = row => {
  if (Number(row?.code) === 914) return Number(row?.montoIvaRetenido ?? 0)
  return Number(
    row?.montoTotal ??
      row?.montoAbono ??
      row?.montoCargo ??
      row?.monto ??
      row?.montoRelacionado ??
      row?.debe ??
      row?.haber ??
      0
  )
}

const getBankMovementType = row => {
  const explicitType = String(row?.tipoMovimiento ?? row?.tipo ?? '')
    .trim()
    .toLowerCase()
  if (explicitType === 'cargo' || explicitType === 'abono') return explicitType

  const movementTable = [row?.tabla, row?.tablaPrincipal, row?.origenTabla]
    .map(Number)
    .find(value => BANCO_ORIGENES.has(value))
  if (movementTable === 7) return 'cargo'
  if (movementTable === 6) return 'abono'

  const cargo = Number(row?.montoCargo ?? row?.debe ?? 0)
  const abono = Number(row?.montoAbono ?? row?.haber ?? 0)
  if (cargo !== 0 && abono === 0) return 'cargo'
  if (abono !== 0 && cargo === 0) return 'abono'

  return null
}

// En la cuenta de anticipo, un cargo bancario aumenta el saldo y un abono lo disminuye.
const getBankAnticipoImpact = row => {
  if (!BANCO_ORIGENES.has(Number(row?.origenTabla))) return 0

  const movementType = getBankMovementType(row)
  const amount = Math.abs(getRowAmount(row))
  if (movementType === 'cargo') return amount
  if (movementType === 'abono') return -amount
  return 0
}

const getAnticipoDate = row => {
  const value = row?.createdAt ?? row?.fechaCreacion ?? row?.fecha
  const timestamp = value ? new Date(value).getTime() : Number.NaN
  return Number.isFinite(timestamp) ? timestamp : Number.MAX_SAFE_INTEGER
}

const getLinkedAmount = row => {
  if (Array.isArray(row?.linkage)) {
    return row.linkage.reduce(
      (total, linkage) => total + Number(linkage?.montoRelacionado ?? 0),
      0
    )
  }
  return Number(row?.linkage?.montoRelacionado ?? row?.montoRelacionado ?? 0)
}

const getSignedAccountBalance = account => {
  if (!account) return 0

  const deudor = Number(account?.deudor ?? 0) || 0
  const acreedor = Number(account?.acreedor ?? 0) || 0
  if (deudor !== 0 || acreedor !== 0) return deudor - acreedor

  const activo = Number(account?.activo ?? 0) || 0
  const pasivo = Number(account?.pasivo ?? 0) || 0
  if (activo !== 0 || pasivo !== 0) return activo - pasivo

  const debe = Number(account?.debe ?? 0) || 0
  const haber = Number(account?.haber ?? 0) || 0
  return debe - haber
}

const isFullyReconciled = row => {
  const status = String(row?.status ?? row?.libro?.status ?? '')
    .trim()
    .toUpperCase()
  if (['CONCILIADO', 'PAGADO', 'COBRADO'].includes(status)) return true

  const amount = Math.abs(getRowAmount(row))
  return amount > 0 && Math.abs(getLinkedAmount(row)) >= amount
}

const ProvisionAgenteAduana = ({
  open,
  setOpen,
  setSwitchDate,
  provisionMode = 'registro',
  dinRevDocuments = EMPTY_ROWS
}) => {
  const { currentBusiness } = useContext(GlobalContext)
  const tablesAPI = useMemo(() => new TablesAPI(), [])
  const organizationAPI = useMemo(() => new OrganizationAPI(), [])
  const [balanceAccount, setBalanceAccount] = useState(null)
  const [fecha, setFecha] = useState(() => new Date().toISOString().split('T')[0])
  const [anticipos, setAnticipos] = useState([])
  const [documentosSii, setDocumentosSii] = useState([])
  const [selectedIds, setSelectedIds] = useState({ sii: [] })
  const [initialDinRevIds, setInitialDinRevIds] = useState([])
  const [loading, setLoading] = useState(true)
  const [isLinking, setIsLinking] = useState(false)
  const isDinRevReconciliation = provisionMode === 'conciliar-din-rev'

  useEffect(() => {
    if (!open || !currentBusiness?.id || !fecha) return

    let active = true
    const selectedDate = new Date(`${fecha}T00:00:00`)
    const year = selectedDate.getFullYear()
    const fechaInicio = `${year}-01-01`
    const fechaFin = fecha

    const loadData = async () => {
      setLoading(true)
      try {
        const [documentsResponse, balanceResponse, anticiposResponse, devolucionesResponse] =
          await Promise.all([
            businessApi.getListSummaryMovementsSpecific({
              businessId: currentBusiness.id,
              type: 'din',
              month: 0,
              year,
              max: 10000,
              page: 0,
              linkage: true
            }),
            tablesAPI.getDataBalance(currentBusiness.id, fechaInicio, fechaFin),
            businessApi.getListSummaryVinculos({
              businessId: currentBusiness.id,
              month: 0,
              year,
              linkageType: '27'
            }),
            businessApi.getListSummaryVinculos({
              businessId: currentBusiness.id,
              month: 0,
              year,
              linkageType: '30'
            })
          ])

        if (!active) return

        const movementRows = toArray(documentsResponse?.data)
        const balanceRows = Array.isArray(balanceResponse?.data)
          ? balanceResponse.data.flat(Infinity).filter(Boolean)
          : []
        setBalanceAccount(
          balanceRows.find(
            row =>
              String(row?.numCuenta ?? row?.codigoCuenta ?? '').trim() ===
              AGENTE_ADUANA_ACCOUNT_CODE
          ) || null
        )
        setAnticipos(
          [...toArray(anticiposResponse?.data), ...toArray(devolucionesResponse?.data)]
            .filter(
              row =>
                MOVIMIENTOS_AGENTE_ADUANA.has(Number(row?.folioCredito)) && getRowId(row) != null
            )
            .sort((a, b) => getAnticipoDate(a) - getAnticipoDate(b) || getRowId(a) - getRowId(b))
        )
        const siiRows = movementRows.filter(row => {
          if (MOVIMIENTOS_AGENTE_ADUANA.has(Number(row?.folioCredito))) return false
          if (isBankMovement(row)) return false

          const origin = String(row?.nombreOrigen ?? row?.origen ?? '').toLowerCase()
          const origenTabla = Number(row?.origenTabla)
          return (
            origin === 'sii' ||
            VINCULACION_ORIGENES.has(origenTabla)
          )
        })

        if (isDinRevReconciliation) {
          const nonBankDinRevDocuments = dinRevDocuments.filter(row => !isBankMovement(row))
          const dinRevIdentifierSet = new Set(
            nonBankDinRevDocuments.flatMap(getRowIdentifiers)
          )
          const preselectedRows = []
          const preselectedIdSet = new Set()

          nonBankDinRevDocuments.forEach(dinRevRow => {
            const identifiers = getRowIdentifiers(dinRevRow)
            const matchingDinRow = siiRows.find(row =>
              getRowIdentifiers(row).some(identifier => identifiers.includes(identifier))
            )
            const row = matchingDinRow || dinRevRow
            const rowId = getRowId(row)
            if (rowId == null || preselectedIdSet.has(String(rowId))) return
            preselectedIdSet.add(String(rowId))
            preselectedRows.push(row)
          })

          const remainingRows = siiRows.filter(
            row =>
              !preselectedIdSet.has(String(getRowId(row))) &&
              !getRowIdentifiers(row).some(identifier => dinRevIdentifierSet.has(identifier))
          )
          const initialIds = preselectedRows.map(getRowId).filter(id => id != null)

          setDocumentosSii([...preselectedRows, ...remainingRows])
          setSelectedIds({ sii: initialIds })
          setInitialDinRevIds(initialIds)
        } else {
          setDocumentosSii(siiRows)
          setSelectedIds({ sii: [] })
          setInitialDinRevIds([])
        }
      } catch (error) {
        console.error('Error al cargar la provisión de agente de aduana:', error)
        if (active) {
          setBalanceAccount(null)
          setAnticipos([])
          setDocumentosSii([])
        }
      } finally {
        if (active) setLoading(false)
      }
    }

    loadData()
    return () => {
      active = false
    }
  }, [
    currentBusiness?.id,
    dinRevDocuments,
    fecha,
    isDinRevReconciliation,
    open,
    tablesAPI
  ])

  useEffect(() => {
    if (!open) {
      setFecha(new Date().toISOString().split('T')[0])
      setSelectedIds({ sii: [] })
      setInitialDinRevIds([])
      setLoading(true)
    }
  }, [open])

  const selectedRows = useMemo(() => {
    const selectedSii = new Set(selectedIds.sii.map(String))
    return [
      ...documentosSii
        .filter(row => selectedSii.has(String(getRowId(row))))
        .map(row => ({ ...row, _provisionSource: 'sii' }))
    ]
  }, [documentosSii, selectedIds])

  const saldo = getSignedAccountBalance(balanceAccount)
  const totalAnticiposBase = useMemo(
    () =>
      anticipos.reduce((total, movimiento) => {
        const monto = Math.abs(getRowAmount(movimiento))
        return Number(movimiento?.folioCredito) === 30
          ? total - monto
          : total + monto
      }, 0),
    [anticipos]
  )
  const totalSeleccionado = selectedRows.reduce((total, row) => total + getRowAmount(row), 0)
  const impactoBancoSeleccionado = selectedRows.reduce(
    (total, row) => total + getBankAnticipoImpact(row),
    0
  )
  const totalAnticipos = totalAnticiposBase + impactoBancoSeleccionado
  const montoNoBancoInicial = useMemo(() => {
    if (!isDinRevReconciliation) return 0

    const initialIdSet = new Set(initialDinRevIds.map(String))
    return documentosSii
      .filter(
        row =>
          initialIdSet.has(String(getRowId(row))) &&
          !BANCO_ORIGENES.has(Number(row?.origenTabla))
      )
      .reduce((total, row) => total + getRowAmount(row), 0)
  }, [documentosSii, initialDinRevIds, isDinRevReconciliation])
  const impactoBancoInicial = useMemo(() => {
    if (!isDinRevReconciliation) return 0

    const initialIdSet = new Set(initialDinRevIds.map(String))
    return documentosSii
      .filter(row => initialIdSet.has(String(getRowId(row))))
      .reduce((total, row) => total + getBankAnticipoImpact(row), 0)
  }, [documentosSii, initialDinRevIds, isDinRevReconciliation])
  const montoNoBancoSeleccionado = selectedRows
    .filter(row => !BANCO_ORIGENES.has(Number(row?.origenTabla)))
    .reduce((total, row) => total + getRowAmount(row), 0)
  const variacionMontoNoBanco = montoNoBancoSeleccionado - montoNoBancoInicial
  const variacionImpactoBanco = impactoBancoSeleccionado - impactoBancoInicial
  const saldoRestante = saldo - variacionMontoNoBanco + variacionImpactoBanco

  const toggleRow = (row, rowSource = row?._provisionSource) => {
    const id = getRowId(row)
    if (id == null || !rowSource) return
    setSelectedIds(current => {
      const sourceIds = current[rowSource] || []
      const nextSourceIds = sourceIds.some(selectedId => String(selectedId) === String(id))
        ? sourceIds.filter(selectedId => String(selectedId) !== String(id))
        : [...sourceIds, id]
      return { ...current, [rowSource]: nextSourceIds }
    })
  }

  const handleClose = () => setOpen(false)

  const handleLink = async () => {
    if (isDinRevReconciliation) {
      const initialIdSet = new Set(initialDinRevIds.map(String))
      const currentIds = [...selectedIds.sii]
      const currentIdSet = new Set(currentIds.map(String))
      const idsToLink = currentIds
        .filter(id => !initialIdSet.has(String(id)))
        .map(Number)
        .filter(Number.isFinite)
      const idsToUnlink = initialDinRevIds
        .filter(id => !currentIdSet.has(String(id)))
        .map(Number)
        .filter(Number.isFinite)

      try {
        setIsLinking(true)
        if (idsToUnlink.length > 0) {
          await organizationAPI.postDesvincular(
            {
              businessId: Number(currentBusiness.id),
              idLibro: 0
            },
            { ids: idsToUnlink }
          )
        }
        if (idsToLink.length > 0) {
          await organizationAPI.postListaVincular(
            {
              businessId: Number(currentBusiness.id),
              idLibro: 0,
              din: true
            },
            { ids: idsToLink }
          )
        }
        sileo.success({
          title: 'Conciliación actualizada',
          description:
            idsToLink.length === 0 && idsToUnlink.length === 0
              ? 'No se realizaron cambios en la selección.'
              : 'Los documentos seleccionados fueron actualizados correctamente.'
        })
        setSwitchDate?.(true)
        handleClose()
      } catch (error) {
        console.error('Error al actualizar la conciliación DIN:', error)
        sileo.error({
          title: 'Error al actualizar',
          description: 'No se pudieron actualizar los documentos conciliados.'
        })
      } finally {
        setIsLinking(false)
      }
      return
    }

    const documentIds = selectedRows
      .map(row => Number(row?.idPrincipal))
      .filter(id => Number.isFinite(id))

    if (documentIds.length === 0) {
      sileo.error({
        title: 'Selecciona documentos',
        description: 'Debes seleccionar al menos un documento SII o una vinculación.'
      })
      return
    }

    const ids = Array.from(new Set(documentIds))

    try {
      setIsLinking(true)
      await organizationAPI.postListaVincular(
        {
          businessId: Number(currentBusiness.id),
          idLibro: 0,
          din: true
        },
        { ids }
      )
      sileo.success({
        title: 'Vinculación realizada',
        description: 'Los documentos fueron vinculados al anticipo correctamente.'
      })
      setSwitchDate?.(true)
      handleClose()
    } catch (error) {
      console.error('Error al vincular la provisión de agente de aduana:', error)
      sileo.error({
        title: 'Error al vincular',
        description: 'No se pudieron vincular los documentos al anticipo.'
      })
    } finally {
      setIsLinking(false)
    }
  }

  return (
    <ResizableModal
      open={open}
      onClose={handleClose}
      size='large'
      footer={
        <>
          <CustomButton
            title='Cerrar'
            color='primary'
            className='rounded-full'
            onClick={handleClose}
            disabled={isLinking}
          />
          <CustomButton
            title={isLinking ? 'Vinculando...' : 'Guardar conciliación'}
            color='accent2'
            className='rounded-full'
            onClick={handleLink}
            disabled={loading || isLinking}
          />
        </>
      }
    >
      {loading ? (
        <div
          className='flex h-full min-h-[420px] items-center justify-center'
          role='status'
          aria-label='Cargando datos'
        >
          <Lottie animationData={LoaderAdm} loop style={{ width: 300, height: 300 }} />
        </div>
      ) : (
        <div className='flex h-full flex-col gap-4 font-sans'>
          <Title className='text-3xl'>Cuenta Corriente Agente de Aduana</Title>

          <div className='grid grid-cols-1 gap-3 rounded-3xl bg-white p-4 shadow-lg md:grid-cols-4'>
            <div>
              <label className='mb-1 block text-xs font-semibold'>Fecha</label>
              <input
                type='date'
                value={fecha}
                onChange={event => {
                  if (event.target.value) setLoading(true)
                  setFecha(event.target.value)
                }}
                className='w-full rounded-full border-gray-300 bg-white text-sm'
              />
            </div>
            <div>
              <label className='mb-1 block text-xs font-semibold'>Cuenta contable</label>
              <input
                disabled
                value={`${AGENTE_ADUANA_ACCOUNT_CODE} ${AGENTE_ADUANA_ACCOUNT_NAME}`}
                className='w-full rounded-full border-gray-200 bg-gray-50 text-sm'
              />
            </div>
            <div>
              <label className='mb-1 block text-xs font-semibold'>
                Totales Anticipo Agente Aduana
              </label>
              <select
                value='todos'
                onChange={() => {}}
                className='w-full rounded-full border-gray-300 bg-white text-sm'
              >
                <option value='todos'>
                  {`Anticipo Agente Aduana - ${formatCurrency(totalAnticipos)}`}
                </option>
                {anticipos.map(anticipo => (
                  <option
                    key={`${anticipo.folioCredito}-${getRowId(anticipo)}`}
                    value={`${anticipo.folioCredito}-${getRowId(anticipo)}`}
                    disabled
                  >
                    {`${
                      MOVIMIENTO_AGENTE_ADUANA_LABELS[Number(anticipo?.folioCredito)]
                    } - ${formatDate(anticipo.fecha ?? anticipo.createdAt)} - ${formatCurrency(
                      getRowAmount(anticipo)
                    )}${isFullyReconciled(anticipo) ? ' - Conciliado' : ''}`}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className='mb-1 block text-xs font-semibold'>Saldo Disponible</label>
              <input
                disabled
                value={formatCurrency(saldo)}
                className={clsx(
                  'w-full rounded-full border-gray-200 bg-gray-50 text-sm font-semibold',
                  saldo < 0 && 'text-danger'
                )}
              />
            </div>
          </div>

          <div className='flex min-h-0 flex-1 gap-4'>
            <div className='flex h-full w-[65%] min-w-0 flex-1 flex-col'>
              <TablaProvisionAgenteAduana
                documentosSii={documentosSii}
                selectedIds={selectedIds}
                onToggle={toggleRow}
                disabled={false}
                loading={loading}
                referenceAmount={saldo}
              />
            </div>

            <aside className='flex h-full w-[35%] min-w-64 flex-col font-sans font-medium'>
              <div className='flex flex-1 flex-col overflow-auto rounded-3xl bg-white p-2 text-[11px] shadow-lg'>
                <div className='mb-1 flex items-start justify-between border-b pb-2'>
                  <div className='flex flex-col'>
                    <p className='text-[12px] font-bold uppercase leading-5'>
                      {AGENTE_ADUANA_ACCOUNT_NAME}
                    </p>
                    <span className='text-[13px] leading-tight'>{AGENTE_ADUANA_ACCOUNT_CODE}</span>
                  </div>
                  <div className='flex flex-col items-end pt-2 text-right'>
                    <span
                      className={clsx(
                        'text-[12px] font-semibold leading-tight',
                        saldo < 0 && 'text-danger'
                      )}
                    >
                      {formatCurrency(saldo)}
                    </span>
                  </div>
                </div>

                {/*<div className='flex items-center justify-between gap-2 border-b py-2'>
                <div>
                  <p className='text-[12px] font-semibold leading-tight'>
                    Balance General
                  </p>
                  <p className='text-[12px] font-semibold leading-tight'>
                    {formatDate(fecha)}
                  </p>
                </div>
                <span className='text-[12px] font-semibold leading-tight text-accent3'>
                  {balanceAccount ? 'ACTUALIZADO' : 'SIN SALDO'}
                </span>
              </div>*/}

                <div className='min-h-0 flex-1 overflow-auto'>
                  {selectedRows.length === 0 ? (
                    <p className='py-5 text-center text-[11px] text-gray-400'>
                      Aún no hay documentos seleccionados.
                    </p>
                  ) : (
                    selectedRows.map(row => (
                      <div
                        key={getRowId(row)}
                        className='flex items-center justify-between border-b py-1 text-gray-700 last:border-0'
                      >
                        <div className='flex min-w-0 items-center gap-3'>
                          <input
                            type='checkbox'
                            checked
                            onChange={() => toggleRow(row)}
                            className='rounded'
                          />
                          <div className='flex min-w-0 flex-col leading-tight'>
                            <span className='truncate'>
                              {row._provisionSource === 'banco'
                                ? row.descripcion || row.nombreCuentaBanco || 'Movimiento bancario'
                                : row.nombreFolio || row.nombreCuenta || 'Documento'}
                            </span>
                            <span className='text-gray-400'>
                              {row._provisionSource === 'banco'
                                ? `${row.nombreCuentaBanco || 'Banco'} · ${
                                    row.numeroCuentaBanco || 'Sin número de cuenta'
                                  } · ${row.fecha ? formatDate(row.fecha) : '-'}`
                                : `Folio ${row.numeroFolio ?? row.folio ?? '-'} · ${
                                    row.fecha ? formatDate(row.fecha) : '-'
                                  }`}
                            </span>
                          </div>
                        </div>
                        <span className='w-20 shrink-0 text-right'>
                          {getConciliationAdjustmentDisplay(row) ? <ConciliationAdjustmentAmount movement={row} formatCurrency={formatCurrency} /> : formatCurrency(getRowAmount(row))}
                        </span>
                      </div>
                    ))
                  )}
                </div>

                <div className='mt-2 shrink-0 border-t border-gray-200 pt-3'>
                  <div className='flex justify-between text-sm font-semibold text-[#000231]'>
                    <span>Monto Saldado</span>
                    <span>{formatCurrency(totalSeleccionado)}</span>
                  </div>
                  <div className='mt-1 flex justify-between text-sm font-semibold text-accent2'>
                    <span>Diferencia</span>
                    <span className={saldoRestante < 0 ? 'text-danger' : ''}>
                      {formatCurrency(saldoRestante)}
                    </span>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      )}
    </ResizableModal>
  )
}

export default ProvisionAgenteAduana
